import { useRegisterSW } from 'virtual:pwa-register/react';
import { useCallback, useState, useEffect } from 'react';

/**
 * サービスワーカー更新のためのカスタムフック
 * @returns サービスワーカー更新関連の関数と状態
 */
export const useServiceWorkerUpdate = () => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const updateSW = useRegisterSW({
    onRegistered(registration) {
      console.log('Service Worker registered', registration);
    },
    onRegisterError(error) {
      console.error('Service Worker registration error', error);
    },
    onNeedRefresh() {
      console.log('New content available, refresh needed');
      setHasUpdate(true);
    },
    onOfflineReady() {
      console.log('App ready to work offline');
    }
  });

  const { needRefresh, updateServiceWorker } = updateSW;

  // needRefreshステートの変更を監視（安全性を向上）
  useEffect(() => {
    if (Array.isArray(needRefresh) && needRefresh[0]) {
      setHasUpdate(true);
    }
  }, [needRefresh]);

  /**
   * registration.installingのstatechangeを監視してwaitingを検知
   */
  const waitForWaitingServiceWorker = (registration: ServiceWorkerRegistration): Promise<boolean> => {
    return new Promise((resolve) => {
      if (registration.waiting) {
        resolve(true);
        return;
      }
      if (registration.installing) {
        const listener = (e: Event) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === 'installed') {
            if (registration.waiting) {
              resolve(true);
            } else {
              resolve(false);
            }
            registration.installing?.removeEventListener('statechange', listener);
          }
        };
        registration.installing.addEventListener('statechange', listener);
      } else {
        resolve(false);
      }
    });
  };

  /**
   * サービスワーカーのアクティベーション完了を待つ
   */
  const waitForServiceWorkerActivation = (): Promise<void> => {
    return new Promise((resolve) => {
      const checkActivation = () => {
        navigator.serviceWorker.ready.then((registration) => {
          if (registration.active) {
            resolve();
          } else {
            // まだアクティベートされていない場合は少し待つ
            setTimeout(checkActivation, 100);
          }
        });
      };
      checkActivation();
    });
  };

  /**
   * サービスワーカーを強制的に更新チェックし、更新があれば適用する
   */
  const forceUpdateServiceWorker = useCallback(async () => {
    console.log('Force checking for service worker updates...');

    try {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration) {
        console.log('Forcing service worker update check...');
        await registration.update();

        // waiting状態のSWが現れるまで最大5回（1秒間）リトライ
        for (let i = 0; i < 5; i++) {
          if (registration.waiting) {
            console.log('Update found: waiting service worker detected', registration.waiting);
            await updateServiceWorker(true);
            setHasUpdate(false);
            return true;
          }
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // 最後にもう一度確認
        if (registration.waiting) {
          console.log('Update found: waiting service worker detected (final check)', registration.waiting);
          await updateServiceWorker(true);
          setHasUpdate(false);
          return true;
        } else {
          console.log('No update found after check', registration);
          return false;
        }
      } else {
        console.log('No service worker registration found');
        return false;
      }
    } catch (err) {
      console.error('Error checking for updates:', err);
      return false;
    }
  }, [updateServiceWorker]);

  /**
   * 更新が必要かどうかをチェック
   */
  const checkForUpdates = useCallback(async () => {
    console.log('Checking for service worker updates...');

    try {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration) {
        await registration.update();

        // installing→installed→waitingをイベントで監視
        const found = await waitForWaitingServiceWorker(registration);

        if (found) {
          console.log('Update found: waiting service worker detected');
          setHasUpdate(true);
          return true;
        } else {
          console.log('No update found after check');
          return false;
        }
      } else {
        console.log('No service worker registration found');
        return false;
      }
    } catch (err) {
      console.error('Error checking for updates:', err);
      return false;
    }
  }, []);

  /**
   * 現在の更新状態を取得
   */
  const getUpdateStatus = useCallback(() => {
    const needRefreshValue = Array.isArray(needRefresh) && needRefresh[0];
    return hasUpdate || needRefreshValue;
  }, [hasUpdate, needRefresh]);

  /**
   * 更新処理を実行（UpdatePrompt用）
   */
  const performUpdate = useCallback(async () => {
    if (isUpdating) {
      console.log('Update already in progress');
      return;
    }

    setIsUpdating(true);

    try {
      console.log('Starting service worker update...');

      // サービスワーカー更新を実行
      await updateServiceWorker(true);

      console.log('Service worker update initiated, waiting for activation...');

      // サービスワーカーのアクティベーション完了を待つ
      await waitForServiceWorkerActivation();

      console.log('Service worker activated successfully');

      // 状態をリセット
      setHasUpdate(false);
      if (Array.isArray(needRefresh) && typeof needRefresh[1] === 'function') {
        needRefresh[1](false);
      }

      // アクティベーション完了後に少し待ってからリロード
      setTimeout(() => {
        console.log('Reloading page...');
        window.location.reload();
      }, 100);

    } catch (error) {
      console.error('Update failed:', error);

      // エラー時は状態をリセット
      setIsUpdating(false);
      setHasUpdate(false);
      if (Array.isArray(needRefresh) && typeof needRefresh[1] === 'function') {
        needRefresh[1](false);
      }

      // エラー時はキャッシュをクリアしてからリロード
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('Cache cleared due to update error');
      } catch (cacheError) {
        console.error('Failed to clear cache:', cacheError);
      }

      // 強制リロード（キャッシュを無視）
      window.location.reload();
    }
  }, [updateServiceWorker, isUpdating, needRefresh]);

  return {
    needRefresh,
    updateServiceWorker: performUpdate,
    forceUpdateServiceWorker,
    checkForUpdates,
    hasUpdate: getUpdateStatus(),
    isUpdating
  };
};
